import 'styled-components/macro'
import type { MouseEvent } from 'react'
import type { Variant as VariantType } from '@velasca/types'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { RoundVariantSelector } from '@velasca/ui/round-variant-selector'
import { ColorSelectorWrapper, Skeleton } from './styles'

interface ColorSelectorProps {
  className?: string
  variants?: VariantType[]
  currentVariant?: VariantType
  onClick?: (variant: VariantType) => void
  loading?: boolean
  showSkeleton?: boolean
  center?: boolean
}

interface VariantProps {
  variant: VariantType
  selected: boolean
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
}

const Variant = ({ variant, selected, onClick }: VariantProps) => {
  const { variantDot, description } = variant

  const title = `${description?.bold ?? ''}${
    description?.bold && description.thin ? ' | ' : ''
  }${description?.thin ?? ''}`

  return (
    <RoundVariantSelector
      title={title}
      color={variantDot}
      selected={selected}
      onClick={onClick}
    />
  )
}

const ColorSelector = ({
  className,
  variants,
  currentVariant,
  onClick,
  loading = false,
  showSkeleton = false,
  center = false,
}: ColorSelectorProps) => {
  return (
    <ColorSelectorWrapper className={className} center={center}>
      {loading
        ? showSkeleton && (
            <Skeleton circle inline count={3} width="3rem" height="3rem" />
          )
        : variants?.map((variant) => {
            const { id, handle } = variant

            return typeof onClick === 'function' ? (
              <Variant
                key={`color-selector-${id}`}
                variant={variant}
                selected={currentVariant?.id === id}
                onClick={(e) => {
                  e.preventDefault()
                  onClick(variant)
                }}
              />
            ) : (
              <ConditionalLink
                key={`color-selector-${id}`}
                href={`/products/${handle ?? ''}`}
                condition={currentVariant?.id !== id}
              >
                <Variant
                  key={id}
                  variant={variant}
                  selected={currentVariant?.id === id}
                />
              </ConditionalLink>
            )
          })}
    </ColorSelectorWrapper>
  )
}

export default ColorSelector
